import { motion } from 'framer-motion';
import React from 'react'
import styled from 'styled-components';
import { pixelCorners, pixelShadow, PixelTag } from '../pixel/pixelUI'
import PixelSprite from '../pixel/PixelSprites'

const Box = styled(motion.li)`
  width: 100%;
  min-height: 31rem;
  position: relative;
  list-style: none;
  background: #fff;
  border: 4px solid ${(props) => props.theme.black};
  ${pixelCorners(10)}
  ${(props) => pixelShadow(7, 7)}
  color: ${(props) => props.theme.black};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: box-shadow 0.15s ease;

  &:hover {
    ${(props) => pixelShadow(11, 11)}

    .spark {
      opacity: 1;
      transform: scale(1) rotate(15deg);
    }
  }
`

const Preview = styled.div`
  position: relative;
  min-height: 13rem;
  overflow: hidden;
  border-bottom: 4px solid ${(props) => props.theme.black};
  background:
    linear-gradient(${(props) => props.$accent}33 1px, transparent 1px),
    linear-gradient(90deg, ${(props) => props.$accent}33 1px, transparent 1px),
    linear-gradient(160deg, #fff 0 48%, ${(props) => props.$accent}55 48% 100%);
  background-size: 18px 18px, 18px 18px, auto;
`

const PreviewHud = styled.div`
  position: absolute;
  inset: 1rem 1rem auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
`

const Level = styled.span`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.58rem;
  color: #fff;
  background: ${(props) => props.theme.black};
  padding: 0.55rem 0.7rem;
`

const Status = styled.span`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.48rem;
  color: ${(props) => props.theme.black};
  background: ${(props) => props.$accent};
  border: 2px solid ${(props) => props.theme.black};
  padding: 0.45rem 0.55rem;
`

const PreviewMark = styled.div`
  position: absolute;
  left: 50%;
  top: 52%;
  transform: translate(-50%, -50%);
  width: 7.2rem;
  height: 7.2rem;
  display: grid;
  place-items: center;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 1.35rem;
  color: ${(props) => props.theme.black};
  background: #fff;
  border: 4px solid ${(props) => props.theme.black};
  ${pixelCorners(12)}
  ${(props) => pixelShadow(7, 7, props.$accent)}
`

const PreviewShot = styled.div`
  position: absolute;
  left: 50%;
  top: 52%;
  transform: translate(-50%, -50%);
  width: 5.7rem;
  height: 8.6rem;
  overflow: hidden;
  background: #fff;
  border: 4px solid ${(props) => props.theme.black};
  ${pixelCorners(7)}
  ${(props) => pixelShadow(6, 6, props.$accent)}

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
`

const Platform = styled.div`
  position: absolute;
  left: 12%;
  right: 12%;
  bottom: 1.1rem;
  height: 0.85rem;
  background: ${(props) => props.$accent};
  border: 3px solid ${(props) => props.theme.black};
  box-shadow: inset 0 -4px 0 ${(props) => props.theme.black}22;
`

const Body = styled.div`
  padding: 1.5rem 1.6rem 1.6rem;
  display: flex;
  flex: 1;
  flex-direction: column;
`

const Spark = styled.div`
  position: absolute;
  top: -0.8rem;
  right: -0.8rem;
  opacity: 0;
  transform: scale(0) rotate(0deg);
  transition: all 0.25s ease;
  z-index: 2;
`

const Role = styled.p`
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.52rem;
  color: ${(props) => props.$accent};
  margin-bottom: 0.65rem;
`

const Title = styled.h2`
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 0.65rem;
`

const Description = styled.p`
  font-size: 0.9rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  line-height: 1.5;
  flex: 1;
`

const Tags = styled.div`
  border-top: 3px dashed ${(props) => props.theme.black};
  padding-top: 0.8rem;
  margin-top: 0.8rem;
  display: flex;
  flex-wrap: wrap;
`

const Footer = styled.footer`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`

const ActionGroup = styled.div`
  position: relative;
  display: inline-flex;
`

const ActionHint = styled.span`
  position: absolute;
  left: 0;
  bottom: calc(100% + 0.55rem);
  width: max-content;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.42rem;
  color: #fff;
  background: ${(props) => props.theme.black};
  border: 2px solid ${(props) => props.$accent};
  padding: 0.4rem 0.5rem;
  opacity: 0;
  transform: translateY(5px);
  pointer-events: none;
  transition: opacity 0.15s ease, transform 0.15s ease;

  ${ActionGroup}:hover &,
  ${ActionGroup}:focus-within & {
    opacity: 1;
    transform: translateY(0);
  }
`

const Link = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-family: ${(props) => props.theme.pixelFont};
  font-size: 0.6rem;
  color: ${(props) => props.$accent};
  text-decoration: none;
  background: ${(props) => props.theme.black};
  border: 3px solid ${(props) => props.theme.black};
  padding: 0.5rem 0.8rem;
  box-shadow: 4px 4px 0 ${(props) => props.$accent};
  transition: color 0.12s ease, background 0.12s ease, box-shadow 0.12s ease, transform 0.12s ease;
  cursor: pointer;

  &::after {
    content: ${(props) => (props.$download ? "'↓'" : "'↗'")};
    font-size: 0.75rem;
  }

  &:hover,
  &:focus-visible {
    color: ${(props) => props.theme.black};
    background: ${(props) => props.$accent};
    box-shadow: 5px 5px 0 ${(props) => props.theme.black};
    transform: translate(-2px, -2px);
    outline: none;
  }
`

const GithubLink = styled.a`
  font-family: 'Karla', sans-serif;
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(props) => props.theme.black};
  text-decoration: underline;
`

// Framer motion configuration
const Item = {
  hidden: { scale: 0 },
  show: { scale: 1, transition: { type: 'spring', duration: 0.5 } },
}

const Card = ({ data, gallery, onOpen }) => {
  const { id, level, name, shortName, status, role, accent, description, tags, demo, download, github } = data;

  return (
    <Box
      key={id}
      variants={Item}
      whileHover={{ x: -4, y: -4, rotate: -1.5, scale: 1.02 }}
      transition={{ type: 'spring', stiffness: 300, damping: 18 }}
    >
      <Spark className="spark">
        <PixelSprite name="sparkle" size={4} color={accent} />
      </Spark>
      <Preview $accent={accent}>
        <PreviewHud>
          <Level>LEVEL {level}</Level>
          <Status $accent={accent}>{status}</Status>
        </PreviewHud>
        {gallery ? (
          <PreviewShot $accent={accent}>
            <img src={gallery.images[0]} alt={`${name} app preview`} loading="lazy" decoding="async" />
          </PreviewShot>
        ) : (
          <PreviewMark $accent={accent}>{shortName}</PreviewMark>
        )}
        <Platform $accent={accent} />
      </Preview>
      <Body>
        <Role $accent={accent}>{role}</Role>
        <Title>{name}</Title>
        <Description>{description}</Description>
        <Tags>
          {tags.map((t, i) => (
            <PixelTag key={i} $bg={i === 0 ? accent : '#FFF1F8'}>#{t}</PixelTag>
          ))}
        </Tags>
        <Footer>
          <ActionGroup>
            <ActionHint $accent={accent}>
              {gallery ? 'VIEW 6 SCREENS' : download ? 'DOWNLOAD ZIP · 36 MB' : 'OPEN PROJECT ↗'}
            </ActionHint>
            {gallery ? (
              <Link
                as="button"
                type="button"
                $accent={accent}
                onClick={() => onOpen(gallery)}
                aria-label={`Open ${name} screenshot gallery`}
                aria-haspopup="dialog"
              >
                VIEW LEVEL
              </Link>
            ) : download ? (
              <Link
                $accent={accent}
                $download
                href={download}
                download="OpenRoad-Assistant-Windows.zip"
                aria-label={`Download ${name} portable app ZIP package`}
              >
                DOWNLOAD APP
              </Link>
            ) : (
              <Link
                $accent={accent}
                href={demo}
                target="_blank"
                rel="noreferrer"
                aria-label={`Open ${name} project in a new tab`}
              >
                VIEW LEVEL
              </Link>
            )}
          </ActionGroup>
          <GithubLink href={github} target="_blank" rel="noreferrer">Source ↗</GithubLink>
        </Footer>
      </Body>
    </Box>
  )
}

export default Card
